/**
 * SharePointFileBrowser — Step 3: browse + select files to import.
 *
 * Iter32 refactor: renders the listed files as a HIERARCHICAL tree
 * (folder → subfolder → files) instead of a flat table. Users can:
 *   • Click a folder checkbox to toggle ALL files inside (recursive).
 *   • Click a file checkbox to toggle just that file.
 *   • Expand / collapse folders to reduce visual clutter on big libraries.
 *
 * Implementation: we build the tree client-side from each file's
 * `relative_path` (already returned by the backend), then flatten it to a
 * linear `rows` array honouring the user's expand/collapse state. We render
 * via `rows.map(...)` instead of recursive JSX so the @emergentbase visual
 * edits Babel plugin doesn't blow its stack on self-referential components.
 */
import { useMemo, useState } from "react";
import { ChevronRight, Folder, FileText, CheckSquare, Square, MinusSquare } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../context/I18nContext";

export default function SharePointFileBrowser({ projectId, isAdmin, form, onImported }) {
  const { t } = useI18n();
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [browsedFolder, setBrowsedFolder] = useState("");
  const [selection, setSelection] = useState({});
  const [importing, setImporting] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { folder_path: true }

  const onBrowse = async () => {
    setFilesLoading(true); setFiles([]); setSelection({}); setCollapsed({});
    try {
      const { listSharePointFiles } = await import("../../lib/api");
      const r = await listSharePointFiles(projectId, { include_subfolders: form.include_subfolders });
      setFiles(r.files || []);
      setBrowsedFolder(r.folder_path || "");
      const initSel = {};
      (r.files || []).forEach((f) => {
        initSel[f.item_id] = { selected: false, custom_name: f.name.replace(/\.[^/.]+$/, ""), set_as_baseline: false };
      });
      setSelection(initSel);
      if (!r.files?.length) toast.info(t("no_files_found"));
      else toast.success(`${r.files.length} ${t("files_found")}`);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setFilesLoading(false); }
  };

  const tree = useMemo(() => buildTree(files, browsedFolder), [files, browsedFolder]);
  const rows = useMemo(() => flattenTree(tree, collapsed), [tree, collapsed]);

  const toggleFile = (id) => setSelection((p) => ({ ...p, [id]: { ...p[id], selected: !p[id]?.selected } }));
  const renameFile = (id, val) => setSelection((p) => ({ ...p, [id]: { ...p[id], custom_name: val } }));
  const toggleBaseline = (id) => setSelection((p) => {
    const next = { ...p };
    const willBe = !next[id]?.set_as_baseline;
    Object.keys(next).forEach((k) => { next[k] = { ...next[k], set_as_baseline: false }; });
    if (willBe) next[id] = { ...next[id], set_as_baseline: true };
    return next;
  });
  const toggleFolder = (folderPath) => {
    const node = findFolder(tree, folderPath);
    if (!node) return;
    const ids = collectFileIds(node).filter((id) => {
      const f = files.find((x) => x.item_id === id);
      return f && !f.already_imported;
    });
    const status = folderSelectionStatus(node, selection, files);
    const willSelect = status !== "all";
    setSelection((p) => {
      const next = { ...p };
      ids.forEach((id) => { if (next[id]) next[id] = { ...next[id], selected: willSelect }; });
      return next;
    });
  };
  const toggleCollapsed = (folderPath) => setCollapsed((p) => ({ ...p, [folderPath]: !p[folderPath] }));

  const selectedCount = Object.values(selection).filter((s) => s.selected).length;

  const onImport = async () => {
    if (!selectedCount) return;
    setImporting(true);
    try {
      const { importSharePointFiles } = await import("../../lib/api");
      const filesPayload = files.filter((f) => selection[f.item_id]?.selected).map((f) => ({
        item_id: f.item_id, custom_name: selection[f.item_id]?.custom_name || undefined,
        set_as_baseline: !!selection[f.item_id]?.set_as_baseline,
      }));
      const r = await importSharePointFiles(projectId, filesPayload);
      if (r.failed?.length) toast.warning(`${r.total_imported}/${r.total_requested} importados. ${r.failed.length} falharam.`);
      else toast.success(`${r.total_imported} versões criadas`);
      onImported?.();
      await onBrowse();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setImporting(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <span className="cs-label">step 3</span>
        <h4 className="font-heading text-sm font-semibold">{t("step_files_title")}</h4>
      </div>

      <div className="flex items-center justify-between mt-4 mb-3">
        <p className="text-xs text-zinc-600">{t("select_files_to_import")}</p>
        <button
          onClick={onBrowse} disabled={filesLoading}
          className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-zinc-800 disabled:opacity-60"
          data-testid="sp-browse-btn"
        >
          {filesLoading ? t("browsing") : t("browse_files")}
        </button>
      </div>

      {files.length > 0 && (
        <>
          <div className="text-[11px] font-mono text-zinc-500 mb-2">{browsedFolder || "/"} · {files.length} {t("files_found")}</div>
          <div className="border border-zinc-200 rounded-md max-h-[480px] overflow-auto" data-testid="sp-files-tree">
            {rows.map((row) => (
              row.kind === "folder"
                ? renderFolderRow(row, tree, files, selection, collapsed, toggleFolder, toggleCollapsed, t)
                : renderFileRow(row, selection, toggleFile, renameFile, toggleBaseline, t)
            ))}
          </div>

          {isAdmin && (
            <div className="flex items-center justify-end gap-2 mt-4">
              <span className="text-xs text-zinc-500 font-mono mr-2">{selectedCount} {t("selected")}</span>
              <button onClick={onImport} disabled={!selectedCount || importing}
                className="inline-flex items-center gap-1.5 bg-cs-accent text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60"
                data-testid="sp-import-btn"
              >
                {importing ? t("importing") : t("import_selected")}
              </button>
            </div>
          )}
        </>
      )}

      {!filesLoading && files.length === 0 && browsedFolder && (
        <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center bg-zinc-50">
          <p className="text-sm text-zinc-500">{t("no_files_found")}</p>
        </div>
      )}
    </div>
  );
}

// ─── flat-row renderers (no JSX recursion) ────────────────────────────────────

function renderFolderRow(row, tree, files, selection, collapsed, toggleFolder, toggleCollapsed, t) {
  const node = findFolder(tree, row.path);
  const status = node ? folderSelectionStatus(node, selection, files) : "none";
  const totalFiles = node ? countFiles(node) : 0;
  const importedFiles = node ? countImportedFiles(node) : 0;
  const isCollapsed = !!collapsed[row.path];
  return (
    <div
      key={`f:${row.path}`}
      className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-zinc-50 border-b border-zinc-100"
      style={{ paddingLeft: 12 + row.depth * 18 }}
      data-testid={`sp-folder-${row.path}`}
    >
      <button
        onClick={() => toggleCollapsed(row.path)}
        className="text-zinc-400 hover:text-zinc-700 shrink-0"
        title={isCollapsed ? t("expand") : t("collapse")}
        data-testid={`sp-folder-toggle-${row.path}`}
      >
        <ChevronRight size={14} className={isCollapsed ? "transition-transform" : "rotate-90 transition-transform"} />
      </button>
      <TriCheckbox status={status} onClick={() => toggleFolder(row.path)} testid={`sp-folder-check-${row.path}`} />
      <Folder size={13} className="text-cs-accent shrink-0" />
      <span className="text-sm font-medium text-zinc-800 truncate flex-1">{row.name}</span>
      <span className="text-[10px] font-mono text-zinc-500 shrink-0">
        {totalFiles} {totalFiles !== 1 ? t("files_short_plural") : t("files_short")}
        {importedFiles > 0 ? ` · ${importedFiles} ${t("already_imported_short")}` : ""}
      </span>
    </div>
  );
}

function renderFileRow(row, selection, toggleFile, renameFile, toggleBaseline, t) {
  const f = row.file;
  const sel = selection[f.item_id] || {};
  const disabled = !!f.already_imported;
  return (
    <div
      key={`fi:${f.item_id}`}
      className={`flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 border-b border-zinc-50 ${disabled ? "opacity-60" : ""}`}
      style={{ paddingLeft: 12 + row.depth * 18 + 18 }}
      data-testid={`sp-file-row-${f.item_id}`}
    >
      <input
        type="checkbox"
        checked={!!sel.selected}
        onChange={() => toggleFile(f.item_id)}
        disabled={disabled}
        className="rounded text-cs-accent focus:ring-cs-accent shrink-0"
        data-testid={`sp-file-check-${f.item_id}`}
      />
      <FileText size={12} className="text-zinc-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-zinc-900 truncate" title={f.name}>{f.name}</div>
        {disabled ? <div className="text-[10px] uppercase tracking-wider text-cs-success">{t("already_imported")}</div> : null}
      </div>
      <input
        type="text"
        value={sel.custom_name || ""}
        onChange={(e) => renameFile(f.item_id, e.target.value)}
        disabled={disabled || !sel.selected}
        placeholder={f.name.replace(/\.[^/.]+$/, "")}
        className="w-44 px-2 py-1 border border-zinc-200 rounded text-[11px] font-mono disabled:bg-zinc-50 disabled:text-zinc-400 shrink-0"
        data-testid={`sp-file-name-${f.item_id}`}
      />
      <span className="text-[10px] font-mono text-zinc-500 shrink-0 w-16 text-right">{(f.size / 1024).toFixed(1)} KB</span>
      <span className="text-[10px] font-mono text-zinc-500 shrink-0 w-20">{(f.last_modified || "").slice(0, 10)}</span>
      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 shrink-0">
        <input
          type="checkbox"
          checked={!!sel.set_as_baseline}
          onChange={() => toggleBaseline(f.item_id)}
          disabled={disabled || !sel.selected}
          className="rounded text-cs-accent focus:ring-cs-accent"
          data-testid={`sp-file-baseline-${f.item_id}`}
        />
        {t("set_as_baseline_short")}
      </label>
    </div>
  );
}

function TriCheckbox({ status, onClick, testid }) {
  const Icon = status === "all" ? CheckSquare : status === "partial" ? MinusSquare : Square;
  const cls = status === "none" ? "text-zinc-400 hover:text-zinc-700" : "text-cs-accent";
  return (
    <button onClick={onClick} className={`${cls} shrink-0`} data-testid={testid}>
      <Icon size={14} />
    </button>
  );
}

// ─── tree helpers ──────────────────────────────────────────────────────────────

function buildTree(files, rootFolder) {
  const root = { kind: "root", name: "", path: "", children: [] };
  const folderMap = new Map();
  folderMap.set("", root);
  const trimRoot = (path) => {
    if (!rootFolder) return path;
    if (path === rootFolder) return "";
    if (path.startsWith(rootFolder + "/")) return path.slice(rootFolder.length + 1);
    return path;
  };
  const sorted = [...files].sort((a, b) => a.relative_path.localeCompare(b.relative_path));
  sorted.forEach((f) => {
    const rel = trimRoot(f.relative_path);
    const segments = rel.split("/").filter(Boolean);
    const fileName = segments.pop();
    let parent = root;
    let runningPath = "";
    segments.forEach((seg) => {
      runningPath = runningPath ? runningPath + "/" + seg : seg;
      let folder = folderMap.get(runningPath);
      if (!folder) {
        folder = { kind: "folder", name: seg, path: runningPath, children: [] };
        folderMap.set(runningPath, folder);
        parent.children.push(folder);
      }
      parent = folder;
    });
    parent.children.push({ kind: "file", name: fileName, file: f });
  });
  const sortChildren = (node) => {
    if (!node.children) return;
    node.children.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    node.children.forEach(sortChildren);
  };
  sortChildren(root);
  return root;
}

/**
 * Walk the tree iteratively and emit a linear list of rows respecting the
 * user's collapse state. Each row carries its `depth` so the render layer
 * can compute indentation.
 */
function flattenTree(root, collapsed) {
  const out = [];
  const stack = [...(root.children || []).map((c) => ({ node: c, depth: 0 }))].reverse();
  while (stack.length) {
    const { node, depth } = stack.pop();
    if (node.kind === "folder") {
      out.push({ kind: "folder", path: node.path, name: node.name, depth });
      if (!collapsed[node.path]) {
        const kids = [...(node.children || [])].reverse();
        kids.forEach((c) => stack.push({ node: c, depth: depth + 1 }));
      }
    } else {
      out.push({ kind: "file", file: node.file, depth });
    }
  }
  return out;
}

function findFolder(root, path) {
  if (!path) return root;
  const stack = [...(root.children || [])];
  while (stack.length) {
    const n = stack.pop();
    if (n.kind === "folder" && n.path === path) return n;
    if (n.children) stack.push(...n.children);
  }
  return null;
}

function collectFileIds(node) {
  if (node.kind === "file") return [node.file.item_id];
  const ids = [];
  const stack = [...(node.children || [])];
  while (stack.length) {
    const n = stack.pop();
    if (n.kind === "file") ids.push(n.file.item_id);
    else if (n.children) stack.push(...n.children);
  }
  return ids;
}

function countFiles(node) {
  let n = 0;
  const stack = [node];
  while (stack.length) {
    const x = stack.pop();
    if (x.kind === "file") n += 1;
    else if (x.children) stack.push(...x.children);
  }
  return n;
}

function countImportedFiles(node) {
  let n = 0;
  const stack = [node];
  while (stack.length) {
    const x = stack.pop();
    if (x.kind === "file") n += x.file.already_imported ? 1 : 0;
    else if (x.children) stack.push(...x.children);
  }
  return n;
}

function folderSelectionStatus(node, selection, files) {
  const ids = collectFileIds(node);
  const selectable = ids.filter((id) => {
    const f = files.find((x) => x.item_id === id);
    return f && !f.already_imported;
  });
  if (selectable.length === 0) return "none";
  const checked = selectable.filter((id) => selection[id]?.selected).length;
  if (checked === 0) return "none";
  if (checked === selectable.length) return "all";
  return "partial";
}
